import type { Dispatch, ReactNode, SetStateAction } from "react";
import { createContext, useContext, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { article } from "~/data";
import { articleSponsorType } from "~/data/article";
import { useArticleUtils } from "~/lib/hooks/article-utils";
import type { contextsContentReadMoreContextProviderContent$key } from "~/queries/__generated__/contextsContentReadMoreContextProviderContent.graphql";

type ContextValue = {
  isClicked: boolean;
  setIsClicked?: Dispatch<SetStateAction<boolean>>;
};

const ContentReadMoreContext = createContext<ContextValue>({
  isClicked: false,
});

type Props = {
  children: ReactNode;
  reference: contextsContentReadMoreContextProviderContent$key;
};

export const ContentReadMoreContextProvider: React.FunctionComponent<Props> = ({
  children,
  reference,
}) => {
  const content = useFragment(
    graphql`
      fragment contextsContentReadMoreContextProviderContent on Content {
        ...articleUtilsArticle
        ... on Article {
          sponsorType
        }
      }
    `,
    reference,
  );

  const { getArticleType } = useArticleUtils();
  const mainArticleType = getArticleType(content);
  const disabledArticleTypes = new Set<string>([
    article.types.live.entityId,
    article.types.studio.entityId,
    article.types.newsletter.entityId,
  ]);
  const isDisableReadMore =
    disabledArticleTypes.has(mainArticleType ?? "") ||
    articleSponsorType.has(content.sponsorType ?? "");

  const [isClicked, setIsClicked] = useState(isDisableReadMore);
  return (
    <ContentReadMoreContext.Provider
      value={{
        isClicked,
        setIsClicked,
      }}
    >
      {children}
    </ContentReadMoreContext.Provider>
  );
};
ContentReadMoreContextProvider.displayName = "ContentReadMoreContextProvider";

export const useContentReadMoreContext = () => useContext(ContentReadMoreContext);
